// todo: remove eslint-disable
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { useMutation, UseMutationResult, useQuery } from 'react-query';
import { AxiosError } from 'axios';
import { FilterOptions } from '../interfaces/filterOptions.interface';
import { generateStatuses } from '../utils';
import { POSITION_DETAILS_MATCH_PAGE_FILTERS, TABLE } from '../consts';
import {
  EngagementStatusEnum, MatchStatusEnum,
} from '../consts/enums';
import { MATCH_PARAMS } from '../consts/requestParamsKeys';
import PaginationResponseDto from '../interfaces/paginationResponse.interface';
import {
  getMatch,
  getMatchListByPositionId,
  getMatchPreview,
  rejectMatch,
  updateLatestTalent,
  updateMatch,
} from '../api/matchApi';
import { getPositionById } from '../api/positionApi';
import { Sort } from '../interfaces/apiRequest.interface';
import { ActiveFilters } from './useTablePageParams';

const REFETCH_INTERVAL = 15000;

type UseMatchListGetFiltersType = {
  matchStatusesOptions: FilterOptions[],
  engageStatusesOptions: FilterOptions[],
}

type UseGetMatchesByPositionIdType = {
  matches: Match[] | [],
  totalCount: number,
  isLoading: boolean,
  refetch: () => void,
  setEnableQuery: (value: boolean) => void,
}

const LIMIT_GET_MATCHES = TABLE.ROWS_PER_PAGE;

export const useMatchListGetFilters = (): UseMatchListGetFiltersType => {
  const [matchStatusesOptions] = useState<FilterOptions[]>(generateStatuses(MatchStatusEnum));
  const [engageStatusesOptions] = useState<FilterOptions[]>(generateStatuses(EngagementStatusEnum));

  return {
    matchStatusesOptions,
    engageStatusesOptions,
  };
};

export const useGetMatchesByPositionId = (
  positionId: string,
  accountId: string,
  activeFilters: ActiveFilters,
  currentPage: number,
  activeSort: Sort,
): UseGetMatchesByPositionIdType => {
  const [enableQuery, setEnableQuery] = useState(true);
  const searchQuery = activeFilters[POSITION_DETAILS_MATCH_PAGE_FILTERS.QUERY]?.[0];

  const {
    data,
    isFetching,
    refetch,
  } = useQuery<PaginationResponseDto<Match>>(
    [`matches_${positionId}`, currentPage, activeFilters, activeSort],
    () => getMatchListByPositionId({
      accountId,
      positionId,
      query: {
        contains: {
          [MATCH_PARAMS.FULL_NAME]: searchQuery,
        },
        in: {
          [MATCH_PARAMS.ENGAGEMENT_STATUS]:
            activeFilters[POSITION_DETAILS_MATCH_PAGE_FILTERS.ENGAGEMENT_STATUS],
          [MATCH_PARAMS.STATUS]: activeFilters[POSITION_DETAILS_MATCH_PAGE_FILTERS.MATCH_STATUS],
        },
        order: activeSort,
        page: currentPage,
        limit: LIMIT_GET_MATCHES,
      },
    }), {
      keepPreviousData: true,
      refetchInterval: REFETCH_INTERVAL,
      enabled: enableQuery,
    });

  return {
    matches: data?.results || [],
    totalCount: data?.totalCount || 0,
    isLoading: isFetching,
    refetch,
    setEnableQuery,
  };
};

export type GetMatchData = (MatchFormattedResponse & { accountId: string });

type useGetMatchResult = {
  match?: GetMatchData,
  isLoading: boolean,
  isFetching: boolean,
  error: unknown,
  refetch: () => void,
}

export const useGetMatch = (
  positionId: string,
  matchId: string,
  callback?: (talent: GetMatchData)=>void,
): useGetMatchResult => {
  const {
    data, isLoading, isFetching, error, refetch,
  } = useQuery<GetMatchData>(
    ['match', matchId, positionId],
    async () => {
      const position = await getPositionById(positionId);
      const match = await getMatch(position.accountId, positionId, matchId);

      return {
        ...match,
        accountId: position.accountId,
      };
    },
    {
      onSuccess: (matchData: GetMatchData) => {
        if (callback) {
          callback(matchData);
        }
      },
    });

  return {
    match: data,
    isLoading,
    isFetching,
    error,
    refetch,
  };
};

type UpdateMatchType = {
  accountId: string,
  positionId:string,
  matchId:string,
  saveObject: Partial<MatchResponse>
}

export const useUpdateMatch = (talentLatest: boolean, successCallback: () => void):
UseMutationResult<unknown, unknown, UpdateMatchType, unknown>  => {
  return useMutation(async (params: UpdateMatchType) => {
    const {
      accountId,
      positionId,
      matchId,
      saveObject,
    } = params;

    if (talentLatest) {
      await updateLatestTalent(accountId, positionId, matchId);
    }

    return updateMatch(accountId, positionId, matchId, saveObject);
  }, {
    onSuccess: () => {
      successCallback();
    },
  });
};

/**
 * Match Preview
 */

 type useGetMatchPreviewResult = {
  data?: MatchPreviewResponse,
  isLoading: boolean,
  isFetching: boolean,
  error: unknown,
  refetch: () => void,
}

export const useGetMatchPreview = (
  accountId: string,
  positionId: string,
  matchId: string | null,
  isEnabled: boolean,
  onSuccessCallback: (data: MatchPreviewResponse) => void,
): useGetMatchPreviewResult => {
  const {
    data, isLoading, isFetching, error, refetch,
  } = useQuery<MatchPreviewResponse>(
    ['match-preview', accountId, positionId, matchId],
    async () => {
      if (!matchId) {
        throw new Error('No matchId in request');
      }

      return getMatchPreview(accountId, positionId, matchId);
    },
    {
      onSuccess: (response) => {
        onSuccessCallback(response);
      },
      enabled: isEnabled,
      refetchInterval: 3000,
    });

  return {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
  };
};

type RejectMatchParams = {
  accountId: string,
  positionId:string,
  matchId:string,
}

export const useRejectMatch = (onSuccessCallback:() => void):
UseMutationResult<unknown, AxiosError, RejectMatchParams>  => {
  return useMutation((params) => {
    const {
      accountId, positionId, matchId,
    } = params;

    return rejectMatch(accountId, positionId, matchId);
  }, {
    onSuccess: () => {
      onSuccessCallback();
    },
  });
};
